import { Alert as StreamAlert, CameraStream, CameraStreamContext, cameraStreamServiceImpl, CheckInResponse, DimensionsOptions, FullScreen, StartStreamResponse, StreamingStatus } from "@lvt/streaming";
import { Box, Grid, IconButton, Link, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Alert } from "@material-ui/lab";
import DetectRTC from "detectrtc";
import { useSnackbar } from "notistack";
import { FC, useCallback, useEffect, useRef, useState } from "react";

import client from "@/api/client";
import useStyles from "@/common/components/LegacyMosaic/Mosaic.styles";
import PresetsButton from "@/common/components/streaming/ActionButtons/PresetsButton/PresetsButton";
import { useSelector } from "@/store";
import CameraStreamData from "@/utilities/types/cameraStreamData";
import { OpenButton } from "@/utilities/types/streaming";


interface MosaicItemProps {
    camera: CameraStreamData;
    streams: number;
    onClose?: () => void;
    isLandscape: boolean;
    liveUnitId: number;
}

const MosaicItem: FC<MosaicItemProps> = ({ camera, streams, onClose = undefined, isLandscape, liveUnitId }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [buttonState, setButtonState] = useState(OpenButton.None);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [height, setHeight] = useState(50);
    const [width, setWidth] = useState(50);
    const [dimensions, setDimensions] = useState<DimensionsOptions>();
    const [startStreamData, setStartStreamData] = useState<StartStreamResponse>();
    const [checkInData, setCheckInData] = useState<CheckInResponse>();
    const [streamingStatus, setStreamingStatus] = useState<StreamingStatus>(StreamingStatus.Initial);
    const [fullScreen, setFullScreen] = useState(false);
    const classes = useStyles();
    const divRef = useRef(null);
    const refObserver = useRef<ResizeObserver>();
    const videoPreference = useSelector((state) => (state.user.videoPreference == "html5" ? "html5" : "webrtc"));

    const cameraStreamService = cameraStreamServiceImpl(client);

    const handleMenu = (button: OpenButton) => {
        setAnchorEl(divRef.current);
        button === buttonState ? setButtonState(OpenButton.None) : setButtonState(button);
    };

    const onAlertMessage = (alert: StreamAlert) => {
        closeSnackbar(alert.type);

        const AlertNotification = (
            <Alert key={alert.type} severity={alert.level}>
                {`${camera.cameraName}: ${alert.message}`}
            </Alert>
        );

        enqueueSnackbar(AlertNotification, {
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
            },
            autoHideDuration: 8000,
            key: alert.message,
            preventDuplicate: true,
            variant: alert.level,
        });
    };

    const onError = (e: any) => {
        console.log("Stream error", e);
    };

    const onStatusChange = (status: StreamingStatus) => {
        // If this check is not present, the onIdle wake up will use stale streaming data
        if (status === StreamingStatus.Idle) setStartStreamData(undefined);

        setStreamingStatus(status);
    };

    const startStream = (cameraId: number) => {
        return cameraStreamService.startStream(cameraId)
            .then((data) => setStartStreamData(data));
    };

    const checkIn = (cameraId: number, streamRecordId: number) => {
        return cameraStreamService.checkIn(cameraId, streamRecordId)
            .then((res) => setCheckInData(res))
            .catch((error) => {
                onAlertMessage({
                    type: "checkInFailed",
                    level: "error",
                    message: "Check-in failed",
                });

                throw error;
            });
    };

    useEffect(() => {
        if (isLandscape) {
            setHeight(100 / Math.floor((Math.sqrt(streams))));
            setWidth(100 / Math.ceil((Math.sqrt(streams))));
        } else {
            setHeight(100 / (streams / 3));
            setWidth(100 / 3);
        }
    }, [streams]);

    const refStreamContainerCb = useCallback((node: HTMLDivElement | null) => {
        if (refObserver.current) {
            refObserver.current.disconnect();
        }

        if (node) {
            const resize = () => {
                const streamBox = node?.getBoundingClientRect();

                setDimensions({ width: streamBox?.width, height: streamBox?.height });
            };

            refObserver.current = new ResizeObserver(resize);

            refObserver.current.observe(node);
            resize();
        }
    }, [streams]);

    const usingHtml5Player = !camera.capabilities?.webrtc || videoPreference == "html5" || !DetectRTC.isWebRTCSupported;

    return (
        <div ref={refStreamContainerCb} style={{ height: `calc(${height}% - 2px)`, width: `calc(${width}% - 2px)`, margin: 1 }}>
            <div className={classes.item}>
                <CameraStream
                    cameraId={camera.cameraId}
                    canWebRtc={camera.capabilities.webrtc}
                    startStream={startStream}
                    startStreamData={startStreamData ?? null}
                    videoPreference={usingHtml5Player ? "html5" : "webrtc"}
                    onAlertMessage={onAlertMessage}
                    onError={onError}
                    dimensionsOptions={dimensions}
                    onStatusChange={onStatusChange}
                    checkIn={checkIn}
                    checkInData={checkInData}
                    checkOut={cameraStreamService.checkOut}
                    inFullScreen={fullScreen}
                >
                    <CameraStreamContext.Consumer>
                        {ctx =>
                        (<div className={classes.fadeControls}>

                            {onClose && <IconButton onClick={onClose} className={classes.closeButton}>
                                <CloseIcon />
                            </IconButton>}

                            <Box className={classes.hoverOverlayContent}>
                                <Box className={classes.overlayText}>
                                    <Link href={`/camera/${camera.cameraId}/stream`} target="_blank" underline="none">
                                        <Typography variant="subtitle1">{camera.liveUnitName}</Typography>
                                        <Typography variant="subtitle2">{camera.cameraName}</Typography>
                                    </Link>
                                </Box>
                            </Box>

                            {streamingStatus === StreamingStatus.Active &&
                                <>
                                    <FullScreen
                                        className={classes.fullScreenButton}
                                        fullScreenRef={ctx.containerRef}
                                        onFullScreenChange={(isFullScreen) => setFullScreen(isFullScreen)}
                                    />

                                    <Box className={classes.hoverOverlayContent}>
                                        <Box className={classes.overlayText}>
                                            <Link href={`/camera/${camera.cameraId}/stream`} target="_blank" underline="none">
                                                <Typography variant="subtitle1">{camera.liveUnitName}</Typography>
                                                <Typography variant="subtitle2">{camera.cameraName}</Typography>
                                            </Link>
                                        </Box>

                                        <Grid container className={classes.actionButtons} spacing={2} ref={divRef}>
                                            {
                                                <PresetsButton
                                                    liveUnitId={liveUnitId}
                                                    handleMenu={handleMenu}
                                                    menuAnchor={anchorEl}
                                                    open={buttonState === OpenButton.Presets}
                                                    presets={camera.presets}
                                                    variant="mosaic"
                                                    maxMenuHeight={dimensions?.height && dimensions.height - 60}
                                                    passedCameraId={camera.cameraId}
                                                />
                                            }
                                        </Grid>
                                    </Box>
                                </>
                            }
                        </div>)
                        }
                    </CameraStreamContext.Consumer>
                </CameraStream>
            </div>
        </div>

    );
};

export default MosaicItem;
